import { ProductListComponent } from '../product-list.component';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { getPriceForProduct } from '@app/api/action/Price';
import { RootReducer, Store } from '@app/app.reducers';
import {
	addProduct,
	getCartItems,
	getShowPricesWithTax,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { ProductFormOrderDialogComponent } from '@app/dialog/components/product-form-order-dialog/product-form-order-dialog.component';
import { ProductFormQuoteDialogComponent } from '@app/dialog/components/product-form-quote-dialog/product-form-quote-dialog.component';
import { DialogService } from '@app/dialog/services/dialog.service';
import { ProductSource } from '@app/product/product-types';
import {
	ProductMediaItem,
	ProductPriceRequest,
	ProductType,
	SelectedOptions,
	getPrice,
	getPriceMap,
	hasFetchedPrice,
} from '@app/product/reducers/product.reducer';
import { ProductWithId, getProductQuantity } from '@app/product/utils/product-utils';
import { getStockStatus } from '@app/product/utils/stock-product';
import { popSuccessToast } from '@app/shared/reducers/toast.reducer';
import { DataLayer } from '@app/shared/services/data-layer.service';
import { NewRelic } from '@app/shared/services/newrelic.service';
import { I18nService } from '@app/shared/utils/i18n.service';
import { hasProp } from '@app/shared/utils/util';
import { getUserPermissions } from '@app/user/reducers/user.reducer';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, map, pluck } from 'rxjs/operators';

export type ProductButton = 'editor' | 'designer' | 'form' | 'cart' | 'details';

/** Displays a list of products with a button to open the editor, designer, or add to cart. */
@UntilDestroy()
@Component({
	selector: 'g-product-list-list',
	templateUrl: './product-list-list.component.html',
	styleUrls: ['./product-list-list.component.scss', '../product-list.component.scss'],
	standalone: false,
})
export class ProductListListComponent extends ProductListComponent implements OnInit {
	protected store: Store<RootReducer.State>;
	protected dataLayer: DataLayer;
	private readonly router = inject(Router);
	private readonly dialogService = inject(DialogService);
	private readonly i18n = inject(I18nService);
	private readonly newRelic = inject(NewRelic);

	readonly productPriceMap$ = this.store.select(getPriceMap);
	productSelectionsMap = new Map<number, SelectedOptions>();
	isProductSelectionValid = new Map<number, boolean>();
	readonly userPermissions$ = this.store.select(getUserPermissions);
	readonly showPrices$ = this.userPermissions$.pipe(map((permissions) => permissions?.show_prices));
	readonly showPricesWithTax$ = this.store.select(getShowPricesWithTax);
	productImages = new Map<number, ProductMediaItem[]>();
	readonly icons = { faSpinner };
	private cartItems: api.CartItemWithProductDto[];

	constructor() {
		super();
		this.store = inject<Store<RootReducer.State>>(Store);
		this.dataLayer = inject(DataLayer);
	}

	ngOnInit(): void {
		// Keep track of cart items for stock level checks
		this.store
			.select(getCartItems)
			.pipe(untilDestroyed(this))
			.subscribe((cartItems) => (this.cartItems = cartItems));

		this.products$.pipe(untilDestroyed(this)).subscribe((products) => {
			for (const product of products) {
				this.productImages.set(
					product.id,
					[product?.images?.master, ...(product?.images?.additional ?? [])].filter(Boolean),
				);
			}
		});
	}

	selectedOptionsChanged(
		event: { isValid: boolean; selectedOptions: SelectedOptions },
		product: api.ProductDto,
	): void {
		const productId = product.id!;
		// Fix for ExpressionChangedAfterItHasBeenCheckedError.
		// TODO: This component should be refactored from top to bottom.
		setTimeout(() => {
			this.isProductSelectionValid.set(productId, event.isValid);
			this.productSelectionsMap.set(productId, event.selectedOptions);
			this.updateProductPrice(product, event);
		});
	}

	updateProductPrice(
		product: api.ProductDto,
		{ isValid, selectedOptions }: { isValid: boolean; selectedOptions: SelectedOptions },
	): void {
		if (!isValid) return;
		if (getProductQuantity({ options: selectedOptions, product }, 0) === 0) {
			this.newRelic.noticeError(new Error('Order quantity is 0, cannot fetch price'), {
				sku: product.sku,
				selectedOptions,
			});
			return;
		}
		const productId = product.id!;
		this.store.dispatch(getPriceForProduct(productId, { body: selectedOptions }, { productId }));
	}

	addProductToCart(product: ProductWithId): void {
		this.store
			.select(getPrice(product.id))
			.pipe(
				first((priceState) => hasFetchedPrice(priceState) && !priceState.isError),
				pluck('price'),
			)
			.subscribe((price) => {
				const id = crypto.randomUUID();
				const selectedOptions = this.productSelectionsMap.get(product.id);
				this.store.dispatch(
					addProduct(
						id,
						product,
						{ workTitle: this.i18n.getText(product.locale.product_name), selectedOptions },
						{
							steps: [],
							productSource: ProductSource.NewProduct,
							productOptionsSavedAt: Date.now(),
						},
						false,
					),
				);
				this.dataLayer.addProductToCart({ id, product, price, options: { selectedOptions } });
				this.store.dispatch(
					popSuccessToast({
						title: `${this.i18n.getText(
							product.locale.product_name,
						)} ${$localize`:@@ToastAddedToCart:added to shopping cart!`}`,
					}),
				);
			});
	}

	openProductFormDialog(product: api.ProductDto): void {
		switch (product?.metadata?.form_type) {
			case 'quote':
				this.dialogService.open(ProductFormQuoteDialogComponent, { product });
				return;
			case 'order':
				this.dialogService.open(ProductFormOrderDialogComponent, { product });
				return;
		}
	}

	/** Which button to show in the product list: Open editor, open designer, add to cart, etc. */
	getButtonType(product: api.ProductDto): ProductButton {
		switch (product?.type) {
			case ProductType.Variable:
				return 'editor';
			case ProductType.Editable:
				return 'designer';
			case ProductType.Form:
				return 'form';
			case ProductType.PrintOnDemand:
			case ProductType.Stock:
				return this.isAValidStockOrPodProduct(product) && (product?.metadata?.allow_checkout ?? true)
					? 'cart'
					: 'details';
			default:
				return 'details';
		}
	}

	isProductButtonDisabled(
		product: api.ProductDto,
		priceState: ProductPriceRequest,
		userPermissions: api.UserGroupPermissionDto,
	): boolean {
		const isProductSelectionValid = this.isProductSelectionValid.get(product.id) !== false;
		const isValidStockOrPodProduct = this.isAValidStockOrPodProduct(product);
		const isInStock = this.hasProductQuantity(product) && this.isInStockForQuantity(product);
		const hasPrice = !priceState.isError && priceState.price;
		const buttonType = this.getButtonType(product);
		const formType = product?.metadata?.form_type;
		return (
			!isProductSelectionValid ||
			(isValidStockOrPodProduct && !isInStock) ||
			(isValidStockOrPodProduct && !hasPrice) ||
			(buttonType === 'form' && formType === 'quote' && !userPermissions?.allow_request_quote)
		);
	}

	async onProductButtonClick(product: ProductWithId): Promise<void> {
		switch (this.getButtonType(product)) {
			case 'editor':
				return this.openProductDetails(product);
			case 'designer':
				return this.openProductDetails(product);
			case 'form':
				return this.openProductFormDialog(product);
			case 'cart':
				return this.addProductToCart(product);
			default:
				return this.openProductDetails(product);
		}
	}

	async openProductDetails(product: api.ProductDto): Promise<void> {
		await this.router.navigate(['/product', product.id, product.slug]);
	}

	async openProductDesigner(product: api.ProductDto): Promise<void> {
		await this.router.navigate(['/product', product.id, product.slug, 'designer']);
	}

	redirectToShoppingCart(): () => Promise<boolean> {
		return () => this.router.navigate(['/checkout/cart']);
	}

	isAValidStockOrPodProduct(product: api.ProductDto): boolean {
		const type = product?.type;
		return (
			(type === ProductType.Stock || type === ProductType.PrintOnDemand) &&
			this.hasQuantityField(product) &&
			this.hasOneSelectableField(product)
		);
	}

	isFormProduct(product: api.ProductDto): boolean {
		return product?.type === ProductType.Form;
	}

	hasOneSelectableField(product: api.ProductDto): boolean {
		let selectablesCount = 0;
		const properties = product?.properties ?? {};
		for (const selectable of Object.keys(properties.selectableOptions)) {
			if (this.isAValidSelectable(properties.selectableOptions[selectable], selectable))
				selectablesCount++;
		}
		for (const field of Object.keys(properties.textFields)) {
			if (this.isAValidTextField(properties.textFields[field])) selectablesCount++;
		}
		return selectablesCount === 1;
	}

	isInStockForQuantity(product: api.ProductDto): boolean {
		if (product.type !== ProductType.Stock) return true;
		return (
			this.isInStock(product) &&
			getStockStatus(product, this.productSelectionsMap.get(product.id), this.cartItems)
		);
	}

	isInStock(product: api.ProductDto): boolean {
		if (product.type !== ProductType.Stock) return true;
		return Number.isFinite(product.stock_level) && product.stock_level > 0;
	}

	private isAValidSelectable(selectable: unknown, key?: string): boolean {
		// Ensure there are 2 or more options to choose from.
		const optionCount =
			(hasProp(selectable, 'selectableValues') &&
				Array.isArray(selectable.selectableValues) &&
				selectable.selectableValues.length) ||
			0;
		// "order_quantity" is a valid selectable even with a single option.
		return key === 'order_quantity' ? optionCount > 0 : optionCount > 1;
	}

	private isAValidTextField(textField: unknown): boolean {
		return hasProp(textField, 'show') && !!textField.show;
	}

	private hasQuantityField(product: api.ProductDto): boolean {
		const properties = product?.properties;
		if (!properties?.selectableOptions || !properties?.textFields) return false;
		return !!properties?.selectableOptions?.order_quantity || properties?.textFields?.quantity?.show;
	}

	private hasProductQuantity(product: api.ProductDto): boolean {
		return this.productSelectionsMap.has(product.id);
	}
}
